import { useFrame } from '@react-three/fiber';
import { useCopperOxideStore, TRIAL_PRESETS } from '../store/useCopperOxideStore';
import LabRoom from '../../lab/shared/LabRoom';
import { ElectronicScale } from '../../lab/shared/ElectronicScale';
import { BoilingTube } from '../../lab/shared/BoilingTube';
import ClampStand from '../../lab/shared/ClampStand';
import RubberStopper from '../../lab/shared/RubberStopper';
import { GlassTube } from '../../lab/shared/GlassTube';
import { SpiritLamp } from '../../lab/shared/SpiritLamp';
import { TripodStand } from '../../lab/shared/TripodStand';
import { PipeclayTriangle } from '../../lab/shared/PipeclayTriangle';
import { ZincGranules } from '../../lab/shared/ZincGranules';
import { CuOPowder } from '../../lab/shared/CuOPowder';
import { HydrogenBubbles } from '../../lab/shared/HydrogenBubbles';
import { RubberTube } from '../../lab/shared/RubberTube';
import {
  useTubeWorldPosition,
  CuOPlacementAnimation,
  ApparatusBuildAnimation,
  CottonBudAnimation,
  ReductionSteam,
} from './CopperOxideAnimations';

export function CopperOxideScene3D() {
  const currentTrial = useCopperOxideStore(s => s.currentTrial);
  const labStage = useCopperOxideStore(s => s.labStage);
  const cuOInTubeG = useCopperOxideStore(s => s.cuOInTubeG);
  const cuInTubeG = useCopperOxideStore(s => s.cuInTubeG);
  const reductionProgress = useCopperOxideStore(s => s.reductionProgress);
  const currentReadingG = useCopperOxideStore(s => s.currentReadingG);
  const tubeTempC = useCopperOxideStore(s => s.tubeTempC);
  const isHeating = useCopperOxideStore(s => s.isHeating);
  const flameIntensity = useCopperOxideStore(s => s.flameIntensity);
  const isH2Flowing = useCopperOxideStore(s => s.isH2Flowing);
  const waterAdded = useCopperOxideStore(s => s.waterAdded);
  const znAdded = useCopperOxideStore(s => s.znAdded);
  const hclAdded = useCopperOxideStore(s => s.hclAdded);
  const apparatusBuilt = useCopperOxideStore(s => s.apparatusBuilt);
  const connected = useCopperOxideStore(s => s.connected);
  const isWeighing = useCopperOxideStore(s => s.isWeighing);
  const weighProgress = useCopperOxideStore(s => s.weighProgress);
  const weighType = useCopperOxideStore(s => s.weighType);
  const isPlacingCuO = useCopperOxideStore(s => s.isPlacingCuO);
  const placeCuOProgress = useCopperOxideStore(s => s.placeCuOProgress);
  const isBuildingApparatus = useCopperOxideStore(s => s.isBuildingApparatus);
  const buildProgress = useCopperOxideStore(s => s.buildProgress);
  const isConnecting = useCopperOxideStore(s => s.isConnecting);
  const connectProgress = useCopperOxideStore(s => s.connectProgress);
  const isApplyingCottonBud = useCopperOxideStore(s => s.isApplyingCottonBud);
  const cottonProgress = useCopperOxideStore(s => s.cottonProgress);
  const tick = useCopperOxideStore(s => s.tick);
  const simulationSpeed = useCopperOxideStore(s => s.simulationSpeed);

  useFrame((_, delta) => {
    tick(delta * simulationSpeed);
  });

  const preset = TRIAL_PRESETS[currentTrial];
  const tubePos = useTubeWorldPosition(isWeighing, weighProgress, weighType);

  // Substance levels animate progressively during the build animation so
  // the user can see the water level rising and the HCl pouring in real
  // time — not a snap at the end of each phase.
  const buildProgressActive = isBuildingApparatus ? buildProgress : 0;

  let waterTubeFill = 0;
  if (waterAdded) {
    waterTubeFill = 0.66;
  } else if (isBuildingApparatus && buildProgressActive > 0) {
    // Phase 1: 0.00 → 0.33 fills the water tube to 0.66 of its height
    waterTubeFill = 0.66 * Math.min(1, buildProgressActive / 0.33);
  }

  let gasTubeFill = 0;
  if (hclAdded) {
    gasTubeFill = 0.35;
  } else if (isBuildingApparatus && buildProgressActive >= 0.66) {
    // Phase 3: 0.66 → 1.00 fills the gas-generator tube to 0.35 of its height
    gasTubeFill = 0.35 * Math.min(1, (buildProgressActive - 0.66) / 0.34);
  }

  // Zinc pile becomes visible once phase 2 is past its halfway point
  const showZincPile = znAdded || (isBuildingApparatus && buildProgressActive >= 0.55);

  // Steam is visible inside the tube during active reduction
  const showSteam = labStage === 'reducing' && tubeTempC > 250;

  return (
    <group>
      <LabRoom />

      {/* LEFT — Gas generator boiling tube on retort stand */}
      <group position={[-1.45, -0.585, -0.2]}>
        <ClampStand visible={true} position={[0.4, 0, 0]} rotation={[0, 0, 0]} armHeight={1.0} />
        {/* Boiling tube hangs from the clamp at [-0.28, 1.0, 0] (-0.68 + 0.4 = -0.28) */}
        <BoilingTube
          position={[-0.28, 0.95, 0]}
          substanceLevel={gasTubeFill}
          stateOfMatter="Liquid"
        />
        {showZincPile && (
          <ZincGranules position={[-0.28, 0.625, 0]} pileRadius={0.038} count={14} />
        )}
        {isH2Flowing && (
          <HydrogenBubbles
            position={[-0.28, 0.66, 0]}
            active={true}
            heightRange={0.18}
            spreadRadius={0.025}
            bubbleCount={30}
          />
        )}
        <RubberStopper
          visible={apparatusBuilt}
          position={[-0.28, 1.40, 0]}
          scale={0.7}
          holeRadius={0.012}
          hideLabel
        />
      </group>

      {/* MIDDLE — tripod stand holding the spirit lamp; the horizontal glass
          tube hovers above the flame so the lamp clearly heats it. Layout:
            tripod base on bench (y = -0.585)
            tripod top platform at y ≈ 0.165 (height 0.75 with scale 0.5)
            pipeclay triangle on top
            spirit lamp sitting on the pipeclay triangle (wick top ≈ y 0.25, flame to y 0.33)
            glass tube hovers at y = 0.35 (just above the flame)        */}
      <group position={[0, -0.585, 0]}>
        <TripodStand position={[0, 0, 0]} scale={0.5} />
        <PipeclayTriangle position={[0, 1.5 * 0.5, 0]} scale={0.7} />
        <SpiritLamp
          position={[0, 1.5 * 0.5 + 0.005, 0]}
          flameOn={isHeating}
          intensity={flameIntensity}
        />
      </group>
      {/* The animated glass tube — sits above the spirit-lamp flame at rest,
          and arcs to the balance pan during weighings */}
      <group position={tubePos}>
        <GlassTube length={0.18} radius={0.016} orientation="horizontal" temperatureC={tubeTempC} visible={true}>
          <CuOPowder
            position={[0, -0.008, 0]}
            massG={cuOInTubeG + cuInTubeG}
            reductionProgress={reductionProgress}
            capacityG={preset.cuoMassG}
            containerRadius={0.016}
          />
        </GlassTube>
        <ReductionSteam active={showSteam} position={[0, 0.012, 0]} />
      </group>

      {/* RIGHT — Water safety bottle on retort stand */}
      <group position={[1.35, -0.585, -0.2]}>
        <ClampStand visible={true} position={[-0.4, 0, 0]} rotation={[0, Math.PI, 0]} armHeight={1.0} />
        <BoilingTube
          position={[0.28, 0.95, 0]}
          substanceLevel={waterTubeFill}
          stateOfMatter="Liquid"
        />
        {/* Bubbles flowing UP through the water column when H₂ is flowing */}
        {isH2Flowing && waterAdded && (
          <HydrogenBubbles
            position={[0.28, 0.62, 0]}
            active={true}
            heightRange={0.30}
            spreadRadius={0.025}
            bubbleCount={50}
          />
        )}
        <RubberStopper
          visible={apparatusBuilt}
          position={[0.28, 1.40, 0]}
          scale={0.7}
          holeRadius={0.012}
          hideLabel
        />
      </group>

      {/* Rubber tubing connecting:
          (a) the gas-generator stopper outlet → left end of the horizontal glass tube
          (b) the right end of the glass tube → the water-safety stopper inlet */}
      <RubberTube
        visible={connected || isConnecting}
        fromPos={[-1.73, 0.85, -0.2]}                              // top of gas-generator stopper
        toPos={[tubePos[0] - 0.09, tubePos[1], tubePos[2]]}        // left end of horizontal glass tube
        midOffset={[0.5, 0.35, 0.1]}
        progress={isConnecting ? connectProgress : 1}
        radius={0.0075}
      />
      <RubberTube
        visible={connected || isConnecting}
        fromPos={[tubePos[0] + 0.09, tubePos[1], tubePos[2]]}      // right end of horizontal glass tube
        toPos={[1.63, 0.85, -0.2]}                                 // top of water-safety stopper
        midOffset={[-0.5, 0.35, 0.1]}
        progress={isConnecting ? connectProgress : 1}
        radius={0.0075}
      />

      {/* Electronic balance on the right-side workbench */}
      <group position={[1.95, -0.585, 0.45]}>
        <ElectronicScale
          position={[0, 0.04, 0]}
          rotation={[0, -Math.PI / 6, 0]}
          readingG={currentReadingG}
        />
      </group>

      {/* Step-specific animations */}
      <CuOPlacementAnimation active={isPlacingCuO} progress={placeCuOProgress} cuoMassG={preset.cuoMassG} />
      <ApparatusBuildAnimation active={isBuildingApparatus} progress={buildProgress} />
      <CottonBudAnimation active={isApplyingCottonBud} progress={cottonProgress} />
    </group>
  );
}

export default CopperOxideScene3D;
